import React, { useState, useRef, useEffect } from 'react'
import { Autocomplete, TextField, Skeleton, IconButton } from '@mui/material'
import ReactToPrint from "react-to-print";
import { useSelector } from "react-redux";
import { viewAllBranchesAPICall } from '../../../../API/Settings/Company Settings/companySettingsAPI';
import { inventoryAdjustmentReportAPICall } from '../../../../API/Report/stockAdjustmentReportAPI'
import { today, convertDateFormat, getCurrentTime, previousDate } from "../../../../Js/Date";
import { vieWareHouseLocationAPICall } from '../../Inventory/Configuration/wareHouseLocationAPI';

export const StockAdjustmentReport = () => {
  let iaReportPrint = useRef()
  
  // Redux selectors
  const allBranchesList = useSelector((state) => state.allBranchesSlice.value);
  const wareHouseLocationList = useSelector((state) => state.wareHouseLocationSlice.value);
  const userRole = useSelector((state) => state.userRoleSlice.value);
  const loginResponse = useSelector((state) => state.loginResponseSlice.value);
  
  // State
  const [fromDate, setFromDate] = useState(previousDate(7)); // Last 7 days
  const [toDate, setToDate] = useState(today);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [reportData, setReportData] = useState([]);
  const [grandTotals, setGrandTotals] = useState({});
  
  useEffect(() => {
    viewAllBranchesAPICall();
    vieWareHouseLocationAPICall();
    
    // Set default branch
    if (loginResponse?.branchPk && allBranchesList) {
      const defaultBranch = allBranchesList.find(b => b._id === loginResponse.branchPk);
      setSelectedBranch(defaultBranch);
    }
  }, []);
  
  // Fetch report data
  const fetchIAReport = async () => {
    if (!selectedBranch || !selectedLocation) {
      alert('Please select Branch and Location');
      return;
    }
    
    const body = {
      branchId: selectedBranch._id,
      locationId: selectedLocation._id,
      fromDate: new Date(fromDate).getTime(),
      toDate: new Date(toDate).getTime()
    };
    
    console.log('📊 Fetching IA Report:', body);
    
    const result = await inventoryAdjustmentReportAPICall(body, setIsLoading);
    
    if (result?.success) {
      setReportData(result.data || []);
      setGrandTotals(result.grandTotals || {});
      console.log('✅ IA Report loaded:', result.data?.length, 'groups');
    } else {
      console.error('❌ IA Report failed');
      setReportData([]);
      setGrandTotals({});
    }
  };
  
  return (
    <div className="global-page-parent-container">
      <p className="breadcrumb">Report &gt; Inventory Adjustment (IA Report)</p>
      
      <div className="daily-report-top-container">
        <h3>Inventory Adjustment Report</h3>
        <hr className="global-hr" />
        
        {/* Filters */}
        <div className="daily-report-top-input-container">
          <div className="global-single-input auto-complete">
            <p>From Date</p>
            <TextField
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              fullWidth
            />
          </div>
          
          <div className="global-single-input auto-complete">
            <p>To Date</p>
            <TextField
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              fullWidth
            />
          </div>
          
          <div className="global-single-input auto-complete">
            <p>Branch</p>
            <Autocomplete
              sx={{ width: "100%" }}
              options={allBranchesList || []}
              getOptionLabel={(option) => `${option?.storeCode}-${option?.branchName}`}
              renderInput={(params) => <TextField {...params} placeholder="Select Branch" />}
              disabled={userRole === "user"}
              value={selectedBranch}
              onChange={(e, newValue) => setSelectedBranch(newValue)}
            />
          </div>
          
          <div className="global-single-input auto-complete">
            <p>Location</p>
            <Autocomplete
              sx={{ width: "100%" }}
              options={wareHouseLocationList || []}
              getOptionLabel={(option) => option?.name}
              renderInput={(params) => <TextField {...params} placeholder="Select Location" />}
              value={selectedLocation}
              onChange={(e, newValue) => setSelectedLocation(newValue)}
            />
          </div>
          
          <button onClick={fetchIAReport} className="btn btn-primary report-submit-btn">
            Generate Report
          </button>
          
          {reportData.length > 0 && (
            <ReactToPrint
              trigger={() => (
                <button className="btn btn-secondary report-submit-btn" style={{ marginLeft: '10px' }}>
                  Print Report
                </button>
              )}
              content={() => iaReportPrint}
            />
          )}
        </div>
      </div>
      
      {/* Report Table */}
      <div className="global-white-bg-container">
        <div className="global-table-container" style={{ overflowX: 'auto', maxHeight: '70vh' }}>
          <table className="global-table" style={{ minWidth: '3000px', fontSize: '12px', borderCollapse: 'collapse' }}>
            <thead style={{ position: 'sticky', top: 0, zIndex: 10, backgroundColor: '#fff' }}>
              <tr style={{ backgroundColor: '#4472C4', color: 'white', fontWeight: 'bold' }}>
                <th style={{ border: '1px solid #ddd', padding: '8px', minWidth: '50px' }}>No</th>
                <th style={{ border: '1px solid #ddd', padding: '8px', minWidth: '100px' }}>Item No</th>
                <th style={{ border: '1px solid #ddd', padding: '8px', minWidth: '200px' }}>Item Name</th>
                <th style={{ border: '1px solid #ddd', padding: '8px', minWidth: '80px' }}>UOM</th>
                <th style={{ border: '1px solid #ddd', padding: '8px', minWidth: '100px' }}>Conversion</th>
                <th style={{ border: '1px solid #ddd', padding: '8px', minWidth: '80px' }}>Rate</th>
                <th style={{ border: '1px solid #ddd', padding: '8px', minWidth: '100px' }}>Opening Stock</th>
                <th style={{ border: '1px solid #ddd', padding: '8px', minWidth: '100px' }}>Purchase</th>
                <th style={{ border: '1px solid #ddd', padding: '8px', minWidth: '100px' }}>Transfer In</th>
                <th style={{ border: '1px solid #ddd', padding: '8px', minWidth: '120px' }}>Total Stock-In Qty</th>
                <th style={{ border: '1px solid #ddd', padding: '8px', minWidth: '130px', backgroundColor: '#70AD47' }}>Total Stock-In Value</th>
                <th style={{ border: '1px solid #ddd', padding: '8px', minWidth: '120px' }}>Purchase Return</th>
                <th style={{ border: '1px solid #ddd', padding: '8px', minWidth: '100px' }}>Transfer Out</th>
                <th style={{ border: '1px solid #ddd', padding: '8px', minWidth: '120px' }}>Raw Wastage Qty</th>
                <th style={{ border: '1px solid #ddd', padding: '8px', minWidth: '100px' }}>SF Wastage</th>
                <th style={{ border: '1px solid #ddd', padding: '8px', minWidth: '140px' }}>Finished Wastage Qty</th>
                <th style={{ border: '1px solid #ddd', padding: '8px', minWidth: '130px' }}>Total Wastage Qty</th>
                <th style={{ border: '1px solid #ddd', padding: '8px', minWidth: '140px', backgroundColor: '#FF6B6B' }}>Total Wastage Value</th>
                <th style={{ border: '1px solid #ddd', padding: '8px', minWidth: '110px' }}>Total Out Qty</th>
                <th style={{ border: '1px solid #ddd', padding: '8px', minWidth: '120px' }}>Ideal Cons Qty</th>
                <th style={{ border: '1px solid #ddd', padding: '8px', minWidth: '130px', backgroundColor: '#FFC000' }}>Ideal Cons Value</th>
                <th style={{ border: '1px solid #ddd', padding: '8px', minWidth: '130px' }}>Book Closing Qty</th>
                <th style={{ border: '1px solid #ddd', padding: '8px', minWidth: '140px', backgroundColor: '#70AD47' }}>Book Closing Value</th>
                <th style={{ border: '1px solid #ddd', padding: '8px', minWidth: '140px' }}>Physical Closing Qty</th>
                <th style={{ border: '1px solid #ddd', padding: '8px', minWidth: '110px' }}>ActCons Qty</th>
                <th style={{ border: '1px solid #ddd', padding: '8px', minWidth: '120px', backgroundColor: '#FFC000' }}>ActCons Value</th>
                <th style={{ border: '1px solid #ddd', padding: '8px', minWidth: '120px' }}>Variance Qty</th>
                <th style={{ border: '1px solid #ddd', padding: '8px', minWidth: '130px', backgroundColor: '#FF6B6B' }}>Variance Value</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                [...Array(10)].map((r, i) => (
                  <tr key={i}>
                    <td colSpan={28}>
                      <Skeleton variant="rectangular" animation="wave" width={"100%"} height={30} />
                    </td>
                  </tr>
                ))
              ) : reportData.length > 0 ? (
                reportData.map((group, groupIndex) => (
                  <React.Fragment key={groupIndex}>
                    {/* Group Header */}
                    <tr style={{ backgroundColor: '#D9E1F2', fontWeight: 'bold', fontSize: '13px' }}>
                      <td colSpan={28} style={{ border: '1px solid #ddd', padding: '10px', textAlign: 'left' }}>
                        {group.itemGroup}
                      </td>
                    </tr>
                    
                    {/* Group Items */}
                    {group.items?.map((item, itemIndex) => (
                      <tr key={`${groupIndex}-${itemIndex}`} style={{ backgroundColor: itemIndex % 2 === 0 ? '#fff' : '#F8F9FA' }}>
                        <td style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'center' }}>{itemIndex + 1}</td>
                        <td style={{ border: '1px solid #ddd', padding: '6px' }}>{item.itemNo}</td>
                        <td style={{ border: '1px solid #ddd', padding: '6px' }}>{item.itemName}</td>
                        <td style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'center' }}>{item.uom}</td>
                        <td style={{ border: '1px solid #ddd', padding: '6px' }}>{item.conversion}</td>
                        <td style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'right' }}>{item.rate}</td>
                        <td style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'right' }}>{item.openingStock}</td>
                        <td style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'right' }}>{item.purchase}</td>
                        <td style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'right' }}>{item.transferIn}</td>
                        <td style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'right' }}>{item.totalStockInQty}</td>
                        <td style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'right', backgroundColor: '#E2EFDA', fontWeight: '500' }}>{item.totalStockInValue}</td>
                        <td style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'right' }}>{item.purchaseReturn}</td>
                        <td style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'right' }}>{item.transferOut}</td>
                        <td style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'right' }}>{item.rawWastageQty}</td>
                        <td style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'right' }}>{item.sfWastage}</td>
                        <td style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'right' }}>{item.finishedWastageQty}</td>
                        <td style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'right' }}>{item.totalWastageQty}</td>
                        <td style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'right', backgroundColor: '#FFE6E6', fontWeight: '500' }}>{item.totalWastageValue}</td>
                        <td style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'right' }}>{item.totalOutQty}</td>
                        <td style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'right' }}>{item.idealConsQty}</td>
                        <td style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'right', backgroundColor: '#FFF2CC', fontWeight: '500' }}>{item.idealConsValue}</td>
                        <td style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'right' }}>{item.bookClosingQty}</td>
                        <td style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'right', backgroundColor: '#E2EFDA', fontWeight: '500' }}>{item.bookClosingValue}</td>
                        <td style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'right' }}>{item.physicalClosingQty}</td>
                        <td style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'right' }}>{item.actConsQty}</td>
                        <td style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'right', backgroundColor: '#FFF2CC', fontWeight: '500' }}>{item.actConsValue}</td>
                        <td style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'right' }}>{item.varianceQty}</td>
                        <td style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'right', backgroundColor: '#FFE6E6', fontWeight: '500' }}>{item.varianceValue}</td>
                      </tr>
                    ))}
                    
                    {/* Group Totals */}
                    <tr style={{ backgroundColor: '#BDD7EE', fontWeight: 'bold' }}>
                      <td colSpan={10} style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>{group.itemGroup} Total</td>
                      <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right', backgroundColor: '#A9D08E' }}>{group.groupTotals?.totalStockInValue}</td>
                      <td colSpan={6} style={{ border: '1px solid #ddd' }}></td>
                      <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right', backgroundColor: '#FF9999' }}>{group.groupTotals?.totalWastageValue}</td>
                      <td style={{ border: '1px solid #ddd' }}></td>
                      <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right', backgroundColor: '#FFD966' }}>{group.groupTotals?.idealConsValue}</td>
                      <td style={{ border: '1px solid #ddd' }}></td>
                      <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right', backgroundColor: '#A9D08E' }}>{group.groupTotals?.bookClosingValue}</td>
                      <td style={{ border: '1px solid #ddd' }}></td>
                      <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right', backgroundColor: '#FFD966' }}>{group.groupTotals?.actConsValue}</td>
                      <td style={{ border: '1px solid #ddd' }}></td>
                      <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right', backgroundColor: '#FF9999' }}>{group.groupTotals?.varianceValue}</td>
                    </tr>
                  </React.Fragment>
                ))
              ) : (
                <tr>
                  <td colSpan={28}>No Data - Please select filters and click Generate Report</td>
                </tr>
              )}
              
              {/* Grand Totals */}
              {reportData.length > 0 && (
                <tr style={{ backgroundColor: '#4472C4', color: 'white', fontWeight: 'bold', fontSize: '14px' }}>
                  <td colSpan={10} style={{ border: '2px solid #000', padding: '10px', textAlign: 'right' }}>GRAND TOTAL</td>
                  <td style={{ border: '2px solid #000', padding: '10px', textAlign: 'right', backgroundColor: '#70AD47' }}>{grandTotals?.totalStockInValue}</td>
                  <td colSpan={6} style={{ border: '2px solid #000' }}></td>
                  <td style={{ border: '2px solid #000', padding: '10px', textAlign: 'right', backgroundColor: '#C00000' }}>{grandTotals?.totalWastageValue}</td>
                  <td style={{ border: '2px solid #000' }}></td>
                  <td style={{ border: '2px solid #000', padding: '10px', textAlign: 'right', backgroundColor: '#FFC000' }}>{grandTotals?.idealConsValue}</td>
                  <td style={{ border: '2px solid #000' }}></td>
                  <td style={{ border: '2px solid #000', padding: '10px', textAlign: 'right', backgroundColor: '#70AD47' }}>{grandTotals?.bookClosingValue}</td>
                  <td style={{ border: '2px solid #000' }}></td>
                  <td style={{ border: '2px solid #000', padding: '10px', textAlign: 'right', backgroundColor: '#FFC000' }}>{grandTotals?.actConsValue}</td>
                  <td style={{ border: '2px solid #000' }}></td>
                  <td style={{ border: '2px solid #000', padding: '10px', textAlign: 'right', backgroundColor: '#C00000' }}>{grandTotals?.varianceValue}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      
      {/* Print Section */}
      <div style={{ position: 'absolute', left: '-9999px', top: 0 }}>
        <div ref={(el) => (iaReportPrint = el)}>
          <div className='print-view-top-container'>
            <div className='print-view-top-left-container' style={{ width: '30%' }}>
              <h6 style={{ margin: "0 0 0 2%" }}>Printed On: {convertDateFormat(today)}&nbsp;{getCurrentTime()}</h6>
            </div>
            <div className='print-view-top-middle-container' style={{ width: '40%' }}>
              {userRole === "admin" ? (
                <h3>{loginResponse?.data?.profile?.companyName}</h3>
              ) : userRole === "user" ? (
                <h3>{loginResponse?.storeName}</h3>
              ) : undefined}
              <p>INVENTORY ADJUSTMENT REPORT</p>
              <p>From: {convertDateFormat(fromDate)} To: {convertDateFormat(toDate)}</p>
            </div>
            <div className='print-view-top-right-container' style={{ width: '30%' }}>
              {userRole === "user" && <p>{loginResponse?.branchId}-{loginResponse?.branchName}</p>}
            </div>
          </div>
          <hr className="print-report-hr" />
          
          {/* Print Table */}
          <table style={{ width: '100%', fontSize: '8px', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #000' }}>
                <th style={{ border: '1px solid #ddd', padding: '4px' }}>No</th>
                <th style={{ border: '1px solid #ddd', padding: '4px' }}>Item No</th>
                <th style={{ border: '1px solid #ddd', padding: '4px' }}>Item Name</th>
                <th style={{ border: '1px solid #ddd', padding: '4px' }}>UOM</th>
                <th style={{ border: '1px solid #ddd', padding: '4px' }}>Conv</th>
                <th style={{ border: '1px solid #ddd', padding: '4px' }}>Rate</th>
                <th style={{ border: '1px solid #ddd', padding: '4px' }}>Open</th>
                <th style={{ border: '1px solid #ddd', padding: '4px' }}>Purch</th>
                <th style={{ border: '1px solid #ddd', padding: '4px' }}>Tr In</th>
                <th style={{ border: '1px solid #ddd', padding: '4px' }}>TotIn Q</th>
                <th style={{ border: '1px solid #ddd', padding: '4px' }}>TotIn V</th>
                <th style={{ border: '1px solid #ddd', padding: '4px' }}>PurRet</th>
                <th style={{ border: '1px solid #ddd', padding: '4px' }}>TrOut</th>
                <th style={{ border: '1px solid #ddd', padding: '4px' }}>RawW</th>
                <th style={{ border: '1px solid #ddd', padding: '4px' }}>SFW</th>
                <th style={{ border: '1px solid #ddd', padding: '4px' }}>FinW</th>
                <th style={{ border: '1px solid #ddd', padding: '4px' }}>TotW Q</th>
                <th style={{ border: '1px solid #ddd', padding: '4px' }}>TotW V</th>
                <th style={{ border: '1px solid #ddd', padding: '4px' }}>TotOut</th>
                <th style={{ border: '1px solid #ddd', padding: '4px' }}>IdealQ</th>
                <th style={{ border: '1px solid #ddd', padding: '4px' }}>IdealV</th>
                <th style={{ border: '1px solid #ddd', padding: '4px' }}>BookQ</th>
                <th style={{ border: '1px solid #ddd', padding: '4px' }}>BookV</th>
                <th style={{ border: '1px solid #ddd', padding: '4px' }}>PhysQ</th>
                <th style={{ border: '1px solid #ddd', padding: '4px' }}>ActQ</th>
                <th style={{ border: '1px solid #ddd', padding: '4px' }}>ActV</th>
                <th style={{ border: '1px solid #ddd', padding: '4px' }}>VarQ</th>
                <th style={{ border: '1px solid #ddd', padding: '4px' }}>VarV</th>
              </tr>
            </thead>
            <tbody>
              {reportData.map((group, groupIndex) => (
                <React.Fragment key={groupIndex}>
                  <tr style={{ backgroundColor: '#f0f0f0', fontWeight: 'bold' }}>
                    <td colSpan={28} style={{ border: '1px solid #ddd', padding: '4px' }}>
                      {group.itemGroup}
                    </td>
                  </tr>
                  {group.items?.map((item, itemIndex) => (
                    <tr key={`${groupIndex}-${itemIndex}`}>
                      <td style={{ border: '1px solid #ddd', padding: '2px', textAlign: 'center' }}>{itemIndex + 1}</td>
                      <td style={{ border: '1px solid #ddd', padding: '2px' }}>{item.itemNo}</td>
                      <td style={{ border: '1px solid #ddd', padding: '2px' }}>{item.itemName}</td>
                      <td style={{ border: '1px solid #ddd', padding: '2px' }}>{item.uom}</td>
                      <td style={{ border: '1px solid #ddd', padding: '2px' }}>{item.conversion}</td>
                      <td style={{ border: '1px solid #ddd', padding: '2px', textAlign: 'right' }}>{item.rate}</td>
                      <td style={{ border: '1px solid #ddd', padding: '2px', textAlign: 'right' }}>{item.openingStock}</td>
                      <td style={{ border: '1px solid #ddd', padding: '2px', textAlign: 'right' }}>{item.purchase}</td>
                      <td style={{ border: '1px solid #ddd', padding: '2px', textAlign: 'right' }}>{item.transferIn}</td>
                      <td style={{ border: '1px solid #ddd', padding: '2px', textAlign: 'right' }}>{item.totalStockInQty}</td>
                      <td style={{ border: '1px solid #ddd', padding: '2px', textAlign: 'right' }}>{item.totalStockInValue}</td>
                      <td style={{ border: '1px solid #ddd', padding: '2px', textAlign: 'right' }}>{item.purchaseReturn}</td>
                      <td style={{ border: '1px solid #ddd', padding: '2px', textAlign: 'right' }}>{item.transferOut}</td>
                      <td style={{ border: '1px solid #ddd', padding: '2px', textAlign: 'right' }}>{item.rawWastageQty}</td>
                      <td style={{ border: '1px solid #ddd', padding: '2px', textAlign: 'right' }}>{item.sfWastage}</td>
                      <td style={{ border: '1px solid #ddd', padding: '2px', textAlign: 'right' }}>{item.finishedWastageQty}</td>
                      <td style={{ border: '1px solid #ddd', padding: '2px', textAlign: 'right' }}>{item.totalWastageQty}</td>
                      <td style={{ border: '1px solid #ddd', padding: '2px', textAlign: 'right' }}>{item.totalWastageValue}</td>
                      <td style={{ border: '1px solid #ddd', padding: '2px', textAlign: 'right' }}>{item.totalOutQty}</td>
                      <td style={{ border: '1px solid #ddd', padding: '2px', textAlign: 'right' }}>{item.idealConsQty}</td>
                      <td style={{ border: '1px solid #ddd', padding: '2px', textAlign: 'right' }}>{item.idealConsValue}</td>
                      <td style={{ border: '1px solid #ddd', padding: '2px', textAlign: 'right' }}>{item.bookClosingQty}</td>
                      <td style={{ border: '1px solid #ddd', padding: '2px', textAlign: 'right' }}>{item.bookClosingValue}</td>
                      <td style={{ border: '1px solid #ddd', padding: '2px', textAlign: 'right' }}>{item.physicalClosingQty}</td>
                      <td style={{ border: '1px solid #ddd', padding: '2px', textAlign: 'right' }}>{item.actConsQty}</td>
                      <td style={{ border: '1px solid #ddd', padding: '2px', textAlign: 'right' }}>{item.actConsValue}</td>
                      <td style={{ border: '1px solid #ddd', padding: '2px', textAlign: 'right' }}>{item.varianceQty}</td>
                      <td style={{ border: '1px solid #ddd', padding: '2px', textAlign: 'right' }}>{item.varianceValue}</td>
                    </tr>
                  ))}
                  <tr style={{ backgroundColor: '#e8f4f8', fontWeight: 'bold' }}>
                    <td colSpan={10} style={{ border: '1px solid #ddd', padding: '4px' }}>{group.itemGroup} Total</td>
                    <td style={{ border: '1px solid #ddd', padding: '4px', textAlign: 'right' }}>{group.groupTotals?.totalStockInValue}</td>
                    <td colSpan={6} style={{ border: '1px solid #ddd' }}></td>
                    <td style={{ border: '1px solid #ddd', padding: '4px', textAlign: 'right' }}>{group.groupTotals?.totalWastageValue}</td>
                    <td style={{ border: '1px solid #ddd' }}></td>
                    <td style={{ border: '1px solid #ddd', padding: '4px', textAlign: 'right' }}>{group.groupTotals?.idealConsValue}</td>
                    <td style={{ border: '1px solid #ddd' }}></td>
                    <td style={{ border: '1px solid #ddd', padding: '4px', textAlign: 'right' }}>{group.groupTotals?.bookClosingValue}</td>
                    <td style={{ border: '1px solid #ddd' }}></td>
                    <td style={{ border: '1px solid #ddd', padding: '4px', textAlign: 'right' }}>{group.groupTotals?.actConsValue}</td>
                    <td style={{ border: '1px solid #ddd' }}></td>
                    <td style={{ border: '1px solid #ddd', padding: '4px', textAlign: 'right' }}>{group.groupTotals?.varianceValue}</td>
                  </tr>
                </React.Fragment>
              ))}
              <tr style={{ backgroundColor: '#d4edda', fontWeight: 'bold', borderTop: '3px solid #000' }}>
                <td colSpan={10} style={{ border: '1px solid #ddd', padding: '4px' }}>GRAND TOTAL</td>
                <td style={{ border: '1px solid #ddd', padding: '4px', textAlign: 'right' }}>{grandTotals?.totalStockInValue}</td>
                <td colSpan={6} style={{ border: '1px solid #ddd' }}></td>
                <td style={{ border: '1px solid #ddd', padding: '4px', textAlign: 'right' }}>{grandTotals?.totalWastageValue}</td>
                <td style={{ border: '1px solid #ddd' }}></td>
                <td style={{ border: '1px solid #ddd', padding: '4px', textAlign: 'right' }}>{grandTotals?.idealConsValue}</td>
                <td style={{ border: '1px solid #ddd' }}></td>
                <td style={{ border: '1px solid #ddd', padding: '4px', textAlign: 'right' }}>{grandTotals?.bookClosingValue}</td>
                <td style={{ border: '1px solid #ddd' }}></td>
                <td style={{ border: '1px solid #ddd', padding: '4px', textAlign: 'right' }}>{grandTotals?.actConsValue}</td>
                <td style={{ border: '1px solid #ddd' }}></td>
                <td style={{ border: '1px solid #ddd', padding: '4px', textAlign: 'right' }}>{grandTotals?.varianceValue}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
